import { Box, Chip, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { memo, useCallback, useMemo } from "react";
import { HeatChargeEvents } from "src/store/api/generatedApi";

dayjs.extend(utc);

const TIME_FORMAT = "HH:mm:ss";

export const LoadingChargeEventsTable = memo(
  ({
    heats,
    onHeatClick,
    colorForHeat,
  }: {
    heats: readonly HeatChargeEvents[];
    onHeatClick?: (heatId: string) => void;
    colorForHeat: (heatId: string) => string;
  }) => {
    const { t } = useTenantTranslation();

    const columns = useMemo<GridColDef<HeatChargeEvents>[]>(
      () => [
        {
          field: "heat_id",
          headerName: t("heat"),
          flex: 1,
          minWidth: 130,
          renderCell: (params) => (
            <Stack
              direction="row"
              sx={{ alignItems: "center", gap: 1, height: "100%" }}
            >
              <Box
                sx={{
                  width: 3,
                  alignSelf: "stretch",
                  minHeight: 16,
                  bgcolor: colorForHeat(params.row.heat_id),
                }}
              />
              {params.row.heat_id}
            </Stack>
          ),
        },
        {
          field: "charges",
          headerName: t("chargeEvents"),
          flex: 3,
          minWidth: 320,
          sortable: false,
          renderCell: (params) => (
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
              {[...params.row.charges]
                .sort((a, b) => a.charge_sequence - b.charge_sequence)
                .map((charge) => (
                  <Chip
                    key={`${charge.consumption_area}-${charge.charge_sequence}`}
                    label={`${charge.consumption_area} · ${dayjs
                      .utc(charge.charged_at)
                      .format(TIME_FORMAT)}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
            </Stack>
          ),
        },
      ],
      [t, colorForHeat]
    );

    const getRowId = useCallback((row: HeatChargeEvents) => row.heat_id, []);

    return (
      <Box
        sx={{
          ...(onHeatClick && { "& .MuiDataGrid-row": { cursor: "pointer" } }),
        }}
      >
        <HotkeyCompliantDataGrid<HeatChargeEvents>
          autoHeight
          hideFooter
          rows={heats}
          columns={columns}
          getRowId={getRowId}
          getRowHeight={() => "auto"}
          onRowClick={(params) => {
            onHeatClick?.((params.row as HeatChargeEvents).heat_id);
          }}
          disableColumnSelector
          disableRowSelectionOnClick
          initialState={{
            sorting: { sortModel: [{ field: "heat_id", sort: "asc" }] },
          }}
        />
      </Box>
    );
  }
);

LoadingChargeEventsTable.displayName = "LoadingChargeEventsTable";
